import React, { useState, useContext, useEffect } from "react";
import companyBasedRequest from "../../../request/companyBasedRequest";
import translations from "../../../translations/translations";
import "./shopInfoModal.css";
import { CombinedContext } from "../../../context/combinedContext";
import RSFNewAutocomplete from "../../../utils/addressAutofill/addressAutocomplete";
import BannerMessage from "../../common/bannerMessage/bannerMessage";

const ShopInfoModal = ({ open, onClose, shopData, setShopData }) => {
    const { language, setUserData, userData } = useContext(CombinedContext);
    const [bannerMessage, setBannerMessage] = useState("");
    const [invalidFields, setInvalidFields] = useState(new Set());
    const [saving, setSaving] = useState(false);

    const [localData, setLocalData] = useState({
        name: "",
        description: "",
        address: "",
        city: "",
        country: "",
        lat: null,
        lng: null,
    });

    useEffect(() => {
        if (shopData) {
            setLocalData({
                name: shopData.name || "",
                description: shopData.description || "",
                address: shopData.address || "",
                city: shopData.city || "",
                country: shopData.country || "",
                lat: shopData.lat ?? null,
                lng: shopData.lng ?? null,
            });
        }
    }, [shopData]);

    if (!open) return null;

    const clearInvalid = (field) => {
        setInvalidFields((prev) => {
            const next = new Set(prev);
            next.delete(field);
            return next;
        });
    };

    const handleSave = async () => {
        const invalid = new Set();
        if (!localData.name.trim()) invalid.add("name");
        if (!localData.description.trim()) invalid.add("description");
        if (!localData.address.trim()) invalid.add("address");
        if (!localData.city.trim()) invalid.add("city");
        if (!localData.country.trim()) invalid.add("country");

        if (invalid.size > 0) {
            setInvalidFields(invalid);
            setBannerMessage(translations[language].profile.errors.fillAllFields || "Please fill in all fields.");
            return;
        }

        setInvalidFields(new Set());
        setBannerMessage("");

        try {
            setSaving(true);
            const res = await companyBasedRequest.updateShopInfo(
                shopData.shopId,
                { ...localData },
                userData.cognitoTokens.AccessToken
            );
            setUserData((prev) => ({ ...prev, shop: res.shop }));
            onClose();
        } catch (err) {
            console.error("Failed to update shop info:", err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="sf-book-overlay" onClick={onClose}>
            <div className="sf-book-modal" onClick={(e) => e.stopPropagation()}>
                <div className="sf-profile-modal-header">
                    <h2 className="sf-profile-modal-title">{translations[language].profile.modal.shopInfo.title}</h2>
                    <button className="sf-book-close" onClick={onClose}>✕</button>
                </div>

                <div className="sf-book-body">
                    <h3>{translations[language].profile.modal.shopInfo.subTitle}</h3>

                    <input
                        className={`sf-book-input ${invalidFields.has("name") ? "sf-shop-info-input-invalid" : ""}`}
                        placeholder={translations[language].profile.modal.shopInfo.name}
                        value={localData.name}
                        onChange={(e) => {
                            setLocalData({ ...localData, name: e.target.value });
                            clearInvalid("name");
                        }}
                    />

                    <div className="sf-description-wrapper">
                        <textarea
                            className={`sf-book-input tall-description ${invalidFields.has("description") ? "sf-shop-info-input-invalid" : ""}`}
                            placeholder={translations[language].profile.modal.shopInfo.description}
                            value={localData.description}
                            maxLength={500}
                            onChange={(e) => {
                                setLocalData({ ...localData, description: e.target.value });
                                clearInvalid("description");
                            }}
                        />
                        <div className="sf-char-count">{localData.description.length}/500</div>
                    </div>

                    <h3>{translations[language].profile.sidebar.location.title}</h3>

                    <div className={invalidFields.has("address") ? "sf-shop-info-input-invalid" : ""} style={{ borderRadius: 8 }}>
                        <RSFNewAutocomplete
                            value={localData.address}
                            onChange={(val) => {
                                setLocalData((prev) => ({ ...prev, address: val, lat: null, lng: null }));
                                clearInvalid("address");
                            }}
                            onSelect={(data) => {
                                setLocalData((prev) => ({
                                    ...prev,
                                    address: data.address,
                                    city: data.city,
                                    country: data.country,
                                    lat: data.lat,
                                    lng: data.lng,
                                }));
                                clearInvalid("address");
                            }}
                            language={language}
                        />
                    </div>

                    <input
                        className={`sf-book-input ${invalidFields.has("city") ? "sf-shop-info-input-invalid" : ""}`}
                        placeholder={translations[language].profile.modal.shopInfo.city}
                        value={localData.city}
                        onChange={(e) => {
                            setLocalData({ ...localData, city: e.target.value });
                            clearInvalid("city");
                        }}
                    />

                    <input
                        className={`sf-book-input ${invalidFields.has("country") ? "sf-shop-info-input-invalid" : ""}`}
                        placeholder={translations[language].profile.modal.shopInfo.country}
                        value={localData.country}
                        onChange={(e) => {
                            setLocalData({ ...localData, country: e.target.value });
                            clearInvalid("country");
                        }}
                    />
                </div>

                <div className="sf-base-footer">
                    <button className="btn ghost" onClick={onClose}>
                        {translations[language].calendar.editor.cancel}
                    </button>
                    <button className="btn" onClick={handleSave} disabled={saving}>
                        {saving
                            ? translations[language].calendar.editor.saving
                            : translations[language].calendar.editor.save}
                    </button>
                </div>
            </div>
            <BannerMessage type="error" text={bannerMessage} />
        </div>
    );
};

export default ShopInfoModal;
